import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  getCatalogoExamesControllerObterQueryKey,
  useCatalogoExamesControllerAtualizarReferencia,
  useCatalogoExamesControllerCriarReferencia,
} from '@/api/generated/admin-catalogo-exames/admin-catalogo-exames';
import type { ReferenciaDto, ReferenciaResponse } from '@/api/generated/model';
import { aplicarErrosDoServidor } from '@/api/erros';
import {
  ESPECIES,
  ROTULO_ESPECIE,
  ROTULO_SEXO,
  SEXOS,
} from '@/features/admin/catalogo-exames/rotulos';
import { Campo, CLASSE_CONTROLE } from '@/shared/components/Campo';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { toast } from '@/shared/ui/sonner';
import { Tabs, TabsList, TabsTrigger } from '@/shared/ui/tabs';

const QUALQUER_SEXO = 'qualquer';

/**
 * ⚠️ O `tipo` não existe no servidor — é o que impede o formulário de montar
 * uma faixa que o servidor recusaria.
 *
 * A regra lá é "numérica OU textual, nunca as duas" (uma faixa com "3,5 a 8" e
 * "negativo" não tem interpretação). Aqui isso vira uma escolha explícita, em
 * vez de um 422 depois de o usuário preencher os dois.
 */
const schema = z
  .object({
    tipo: z.enum(['numerica', 'textual']),
    especie: z.enum(ESPECIES),
    sexo: z.string(),
    idadeMinMeses: z.string(),
    idadeMaxMeses: z.string(),
    minimo: z.string(),
    maximo: z.string(),
    textoReferencia: z.string().trim().max(200),
    observacao: z.string().trim().max(500),
  })
  .superRefine((valores, ctx) => {
    if (valores.tipo === 'textual') {
      if (!valores.textoReferencia) {
        ctx.addIssue({
          code: 'custom',
          path: ['textoReferencia'],
          message: 'Informe o texto da referência.',
        });
      }
      return;
    }

    const min = numeroOuNulo(valores.minimo);
    const max = numeroOuNulo(valores.maximo);

    if (min === null && max === null) {
      ctx.addIssue({ code: 'custom', path: ['minimo'], message: 'Informe mínimo e/ou máximo.' });
    }
    if (min !== null && max !== null && min > max) {
      ctx.addIssue({
        code: 'custom',
        path: ['maximo'],
        message: 'O máximo não pode ser menor que o mínimo.',
      });
    }

    const idadeMin = numeroOuNulo(valores.idadeMinMeses);
    const idadeMax = numeroOuNulo(valores.idadeMaxMeses);
    if (idadeMin !== null && idadeMax !== null && idadeMin > idadeMax) {
      ctx.addIssue({
        code: 'custom',
        path: ['idadeMaxMeses'],
        message: 'A idade máxima não pode ser menor que a mínima.',
      });
    }
  });

type Formulario = z.infer<typeof schema>;

/** Aceita vírgula: o admin digita "3,5", não "3.5". */
function numeroOuNulo(texto: string): number | null {
  const limpo = texto.trim().replace(',', '.');
  if (!limpo) return null;
  const valor = Number(limpo);
  return Number.isFinite(valor) ? valor : null;
}

interface Props {
  aberto: boolean;
  aoFechar: () => void;
  exameId: string;
  analitoId: string;
  /** Ausente = criação. */
  referencia?: ReferenciaResponse;
}

export function ReferenciaFormDialogo({
  aberto,
  aoFechar,
  exameId,
  analitoId,
  referencia,
}: Props) {
  const queryClient = useQueryClient();
  const editando = Boolean(referencia);

  const criar = useCatalogoExamesControllerCriarReferencia();
  const atualizar = useCatalogoExamesControllerAtualizarReferencia();

  const {
    register,
    control,
    watch,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<Formulario>({ resolver: zodResolver(schema) });

  const tipo = watch('tipo');

  useEffect(() => {
    if (!aberto) return;

    const texto = (v: number | null | undefined) =>
      v === null || v === undefined ? '' : String(v).replace('.', ',');

    reset({
      tipo: referencia?.textoReferencia ? 'textual' : 'numerica',
      especie: (referencia?.especie as Formulario['especie']) ?? 'CANINO',
      sexo: referencia?.sexo ?? QUALQUER_SEXO,
      idadeMinMeses: texto(referencia?.idadeMinMeses),
      idadeMaxMeses: texto(referencia?.idadeMaxMeses),
      minimo: texto(referencia?.minimo),
      maximo: texto(referencia?.maximo),
      textoReferencia: referencia?.textoReferencia ?? '',
      observacao: referencia?.observacao ?? '',
    });
  }, [aberto, referencia, reset]);

  const aoEnviar = handleSubmit(async (dados) => {
    const numerica = dados.tipo === 'numerica';

    const corpo: ReferenciaDto = {
      especie: dados.especie,
      sexo: dados.sexo === QUALQUER_SEXO ? null : (dados.sexo as ReferenciaDto['sexo']),
      idadeMinMeses: numeroOuNulo(dados.idadeMinMeses),
      idadeMaxMeses: numeroOuNulo(dados.idadeMaxMeses),
      minimo: numerica ? numeroOuNulo(dados.minimo) : null,
      maximo: numerica ? numeroOuNulo(dados.maximo) : null,
      textoReferencia: numerica ? null : dados.textoReferencia,
      observacao: dados.observacao || null,
    };

    try {
      if (referencia) {
        await atualizar.mutateAsync({
          id: exameId,
          analitoId,
          referenciaId: referencia.id,
          data: corpo,
        });
      } else {
        await criar.mutateAsync({ id: exameId, analitoId, data: corpo });
      }

      await queryClient.invalidateQueries({
        queryKey: getCatalogoExamesControllerObterQueryKey(exameId),
      });
      toast.success(editando ? 'Faixa atualizada.' : 'Faixa adicionada.');
      aoFechar();
    } catch (erro) {
      aplicarErrosDoServidor(erro, setError, 'Não foi possível salvar a faixa.');
    }
  });

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{editando ? 'Editar faixa' : 'Nova faixa de referência'}</DialogTitle>
          <DialogDescription>
            Uma faixa por espécie (e por sexo/idade, quando muda). É ela que decide se o resultado
            sai marcado como alterado no laudo.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={aoEnviar} className="space-y-4" noValidate>
          {errors.root?.message && (
            <Alert variant="destructive">
              <AlertDescription>{errors.root.message}</AlertDescription>
            </Alert>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Campo id="especie" rotulo="Espécie" erro={errors.especie?.message} obrigatorio>
              <Controller
                control={control}
                name="especie"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger id="especie" className={CLASSE_CONTROLE}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ESPECIES.map((especie) => (
                        <SelectItem key={especie} value={especie}>
                          {ROTULO_ESPECIE[especie]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </Campo>

            <Campo id="sexo" rotulo="Sexo" erro={errors.sexo?.message}>
              <Controller
                control={control}
                name="sexo"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger id="sexo" className={CLASSE_CONTROLE}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={QUALQUER_SEXO}>Qualquer</SelectItem>
                      {SEXOS.map((sexo) => (
                        <SelectItem key={sexo} value={sexo}>
                          {ROTULO_SEXO[sexo]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </Campo>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Campo
              id="idadeMinMeses"
              rotulo="Idade mínima (meses)"
              erro={errors.idadeMinMeses?.message}
            >
              <Input
                id="idadeMinMeses"
                inputMode="numeric"
                className={CLASSE_CONTROLE}
                {...register('idadeMinMeses')}
              />
            </Campo>

            <Campo
              id="idadeMaxMeses"
              rotulo="Idade máxima (meses)"
              erro={errors.idadeMaxMeses?.message}
              dica="Em branco = vale para qualquer idade."
            >
              <Input
                id="idadeMaxMeses"
                inputMode="numeric"
                className={CLASSE_CONTROLE}
                {...register('idadeMaxMeses')}
              />
            </Campo>
          </div>

          <Controller
            control={control}
            name="tipo"
            render={({ field }) => (
              <Tabs value={field.value} onValueChange={field.onChange}>
                <TabsList className="w-full">
                  <TabsTrigger value="numerica" className="flex-1">
                    Faixa numérica
                  </TabsTrigger>
                  <TabsTrigger value="textual" className="flex-1">
                    Resultado qualitativo
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            )}
          />

          {tipo === 'textual' ? (
            <Campo
              id="textoReferencia"
              rotulo="Referência"
              erro={errors.textoReferencia?.message}
              obrigatorio
              dica='Ex.: "Ausente", "Negativo".'
            >
              <Input
                id="textoReferencia"
                className={CLASSE_CONTROLE}
                {...register('textoReferencia')}
              />
            </Campo>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <Campo id="minimo" rotulo="Mínimo" erro={errors.minimo?.message}>
                <Input
                  id="minimo"
                  inputMode="decimal"
                  placeholder="3,5"
                  className={CLASSE_CONTROLE}
                  {...register('minimo')}
                />
              </Campo>

              <Campo
                id="maximo"
                rotulo="Máximo"
                erro={errors.maximo?.message}
                dica="Um dos dois basta."
              >
                <Input
                  id="maximo"
                  inputMode="decimal"
                  placeholder="8,25"
                  className={CLASSE_CONTROLE}
                  {...register('maximo')}
                />
              </Campo>
            </div>
          )}

          <Campo id="observacao" rotulo="Observação" erro={errors.observacao?.message}>
            <Input id="observacao" className={CLASSE_CONTROLE} {...register('observacao')} />
          </Campo>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={aoFechar} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editando ? 'Salvar' : 'Adicionar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
